/**
 * ============================================================================
 * MARK DELIVERED COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for seller to mark transaction as delivered
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../../hooks/useTransaction';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Spinner from '../common/Spinner';
import Checkbox from '../common/Checkbox';
import ProgressBar from '../common/ProgressBar';

/**
 * Mark Delivered Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {Object} props.transaction - Transaction object (optional)
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const MarkDelivered = ({ transactionId, transaction: propTransaction, onSuccess }) => {
  const navigate = useNavigate();
  const { getTransaction, markAsDelivered, loading } = useTransaction();
  const { uploadFile } = useStorage();
  const { showSuccess, showError } = useToast();
  const { formatCurrency, language } = useI18n();

  const [transaction, setTransaction] = useState(propTransaction || null);
  const [isLoading, setIsLoading] = useState(!propTransaction);
  const [formData, setFormData] = useState({
    deliverablesUrl: [],
    deliverablesNotes: '',
  });
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const loadTransaction = async () => {
      if (propTransaction) return;
      try {
        setIsLoading(true);
        const data = await getTransaction(transactionId);
        setTransaction(data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement' : 'Failed to load transaction')
        );
        console.error('Load transaction error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (transactionId) {
      loadTransaction();
    }
  }, [transactionId, propTransaction, getTransaction, showError, language]);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const uploaded = await uploadFile(file, {
          folder: 'deliverables',
          publicId: `deliverable_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        });
        setUploadProgress(((index + 1) / selectedFiles.length) * 100);
        return uploaded.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData((prev) => ({
        ...prev,
        deliverablesUrl: [...prev.deliverablesUrl, ...uploadedUrls],
      }));
      showSuccess(
        language === 'fr'
          ? `${uploadedUrls.length} fichier(s) téléchargé(s)`
          : `${uploadedUrls.length} file(s) uploaded`
      );
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec du téléchargement' : 'Failed to upload files'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      deliverablesUrl: prev.deliverablesUrl.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.deliverablesUrl.length === 0) {
      setErrors({ deliverablesUrl: language === 'fr' ? 'Au moins un fichier est requis' : 'At least one file is required' });
      return;
    }

    if (!confirmed) {
      setErrors({ confirmed: language === 'fr' ? 'Veuillez confirmer la livraison' : 'Please confirm delivery' });
      return;
    }

    try {
      await markAsDelivered(transactionId, {
        deliverablesUrl: formData.deliverablesUrl,
        deliverablesNotes: formData.deliverablesNotes.trim() || undefined,
      });
      showSuccess(
        language === 'fr' ? 'Transaction marquée comme livrée!' : 'Transaction marked as delivered!'
      );
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/transactions/${transactionId}`);
      }
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de la livraison' : 'Failed to mark as delivered')
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto">

        {/* Transaction Info */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Transaction' : 'Transaction'}
              </p>
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {transaction.title}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Montant' : 'Amount'}
              </p>
              <p className="text-lg font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
              </p>
            </div>
          </div>
        </Card>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <label className="block text-lg font-black text-gray-900 dark:text-white mb-4">
              {language === 'fr' ? 'Télécharger des fichiers/preuves' : 'Upload Files/Proof'}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <label className="block">
              <input
                type="file"
                multiple
                accept="*/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              <div className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 group">
                <div className="text-center">
                  {uploading ? (
                    <>
                      <Spinner size="md" className="mb-3" />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {language === 'fr' ? 'Téléchargement...' : 'Uploading files...'}
                      </span>
                      {uploadProgress > 0 && (
                        <div className="mt-3 w-48">
                          <ProgressBar value={uploadProgress} variant="info" animated showLabel />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-5xl mb-3 block group-hover:scale-110 transition-transform">
                        📎
                      </span>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {language === 'fr' ? 'Choisir des fichiers' : 'Choose Files'}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {language === 'fr'
                          ? 'Glisser-déposer ou cliquer pour sélectionner'
                          : 'Drag and drop or click to select'}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </label>
            {errors.deliverablesUrl && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.deliverablesUrl}</p>
            )}

            {/* Uploaded Files */}
            {formData.deliverablesUrl.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.deliverablesUrl.map((url, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700"
                  >
                    <span className="text-xl">📎</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {url.split('/').pop() || `File ${index + 1}`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {url}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      ✕
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </Card>

          {/* Delivery Notes */}
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            <Textarea
              label={language === 'fr' ? 'Notes de livraison (optionnel)' : 'Delivery Notes (optional)'}
              value={formData.deliverablesNotes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, deliverablesNotes: e.target.value }))
              }
              placeholder={
                language === 'fr'
                  ? 'Ajoutez des notes sur la livraison...'
                  : 'Add notes about the delivery...'
              }
              rows={5}
            />
          </Card>

          {/* Confirmation Checkbox */}
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <Checkbox
              checked={confirmed}
              onChange={(e) => {
                setConfirmed(e.target.checked);
                setErrors((prev) => ({ ...prev, confirmed: null }));
              }}
              label={
                language === 'fr'
                  ? 'Je confirme la livraison selon les termes convenus'
                  : 'I confirm delivery as per agreed terms'
              }
              error={errors.confirmed}
            />
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="gradient"
            size="lg"
            fullWidth
            loading={loading || uploading}
            className="text-lg py-6 shadow-xl hover:shadow-2xl transition-all animate-fadeInUp"
            style={{ animationDelay: '0.5s' }}
          >
            {language === 'fr' ? 'Confirmer la livraison' : 'Confirm Delivery'} →
          </Button>
        </form>
    </div>
  );
};

export default MarkDelivered;
